import { handleApiError } from 'helpers/handleApiError';
import { API_BASE_URL } from '../config';

const baseUrl = API_BASE_URL;

// Helper to add UTM parameters to URL (now expects UTMs to be in formData)
const addUTMParamsToUrl = (url, formData) => {
  try {
    const { 
      utm_source, 
      utm_medium, 
      utm_campaign, 
      utm_content, 
      utm_term,
      utm_id,
      gclid,
      ...rest 
    } = formData || {};
    
    const urlObj = new URL(url, window.location.origin);
    
    // Add UTM parameters to query string
    if (utm_source && utm_source !== '(direct)') urlObj.searchParams.append('utm_source', utm_source);
    if (utm_medium && utm_medium !== '(none)') urlObj.searchParams.append('utm_medium', utm_medium);
    if (utm_campaign) urlObj.searchParams.append('utm_campaign', utm_campaign);
    if (utm_content) urlObj.searchParams.append('utm_content', utm_content);
    if (utm_term) urlObj.searchParams.append('utm_term', utm_term);
    if (utm_id) urlObj.searchParams.append('utm_id', utm_id);
    if (gclid) urlObj.searchParams.append('gclid', gclid);
    
    console.log('[Booking Service] URL with UTMs:', {
      original: url,
      withUtms: urlObj.toString(),
      hasUtms: urlObj.search.length > 0,
      hasGclid: !!gclid
    });
    
    return {
      url: urlObj.toString(),
      data: rest // Return data without UTM params (they're in URL)
    };
  } catch (error) {
    console.error('[Booking Service] Error building URL:', error);
    return { url, data: formData };
  }
};

export const transformBookingData = (data, step) => {
  if (
    !data ||
    !Array.isArray(data.booking_days) ||
    data.booking_days.length === 0
  ) {
    return {};
  }

  const extractedBookingHours = data.booking_days.flatMap((day) => {
    if (!day.booking_hours || !Array.isArray(day.booking_hours)) {
      console.warn(`Missing or invalid booking_hours for date: ${day.date}`);
      return [];
    }

    return day.booking_hours.map((hour) => ({
      date: day.date,
      from: hour.from?.slice(0, 5) || "00:00",
      to: hour.to?.slice(0, 5) || "00:00",
    }));
  });

  const transformedDayServices = data.booking_days
    .filter((day) => day.id)
    .map((day) => ({
      day_id: day.id,
      services: Array.isArray(day.booking_services)
        ? day.booking_services.map((service) => ({
            service_id: service.place_service_id,
            quantity: service.quantity,
          }))
        : [],
    }));

  return {
    booking_hours: extractedBookingHours,
    day_services: transformedDayServices,
  };
};

export const postQuote = async (data) => {
  try {
    const response = await fetch(`${baseUrl}quote`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      const error = new Error("Failed to submit the form.");
      error.data = result.errors || [];
      throw error;
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const validateSlot = async (slot, spaceID) => {
  const payload = {
    date: slot.date,
    start_hour: slot.from,
    end_hour: slot.to,
    spaceID: spaceID,
  };

  try {
    const response = await fetch(`${baseUrl}bookings/validate-dates`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (response.ok && data?.data?.valid) {
      return { isValid: true, data };
    } else {
      return { isValid: false, message: data.message || "Invalid time slot." };
    }
  } catch (error) {
    console.error("Error validating slot:", error);
    return {
      isValid: false,
      message: "An error occurred while validating the slot.",
    };
  }
};

export const postBookingStepByStep = async (
  formData,
  token = null,
  step = 1,
  bookingId = null
) => {
  // Build base URL
  let baseApiUrl;
  if (bookingId) {
    baseApiUrl = `${baseUrl}bookings/steps/${step}?id=${bookingId}`;
  } else {
    baseApiUrl = `${baseUrl}bookings/steps/${step}`;
  }
  
  // Add UTM parameters to URL
  const { url: finalUrl, data: requestData } = addUTMParamsToUrl(baseApiUrl, formData);
  
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const body = JSON.stringify(requestData);

  console.log('[postBookingStepByStep] API Call:', {
    url: finalUrl,
    step,
    bookingId,
    hasUtmParams: finalUrl.includes('utm_'),
    hasGclid: finalUrl.includes('gclid')
  });

  try {
    const response = await fetch(finalUrl, {
      method: "POST",
      headers: headers,
      body: body,
    });
    const data = await response.json();

    if (!response.ok) {
      const error = new Error("Failed to submit the form.");
      error.data = data.errors || [];
      throw error;
    }
    
    const transformedData = {
      ...data.data,
    };
    return transformedData;
  } catch (error) {
    console.error("Error posting booking step:", error);
    throw error;
  }
};

export const calculateBookingCost = async (
  formData,
  token = null,
  step = 1,
  bookingId = null,
  lang = "en"
) => {
  // Build base URL
  let baseApiUrl = `${baseUrl}bookings/calculate/${step}`;
  if (bookingId) {
    baseApiUrl += `?id=${bookingId}`;
  }
  
  // Add UTM parameters to URL
  const { url: finalUrl, data: requestData } = addUTMParamsToUrl(baseApiUrl, formData);
  
  const headers = {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : undefined,
  };

  const body = JSON.stringify(requestData);

  console.log('[calculateBookingCost] API Call:', {
    url: finalUrl,
    step,
    bookingId,
    hasUtmParams: finalUrl.includes('utm_'),
    hasGclid: finalUrl.includes('gclid')
  });

  try {
    const response = await fetch(finalUrl, {
      method: "POST",
      headers: headers,
      body: body,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error("Failed to calculate the booking cost.");
      error.data = data.errors || [];
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error calculating booking cost:", error);
    throw error;
  }
};

export const fetchServiceOptions = async (bookingId, lang = "en") => {
  const apiUrl = `${baseUrl}bookings/${bookingId}/services?lang=${lang}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch services: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching service options:", error);
    return null;
  }
};

export const getBookingById = async (bookingId, token, step = 1) => {
  try {
    const response = await fetch(`${baseUrl}bookings/${bookingId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    const transformedData = {
      ...data.data,
      ...transformBookingData(data.data, step),
    };

    return transformedData;
  } catch (error) {
    console.error("Error fetching booking details:", error);
    throw error;
  }
};

export const deleteBooking = async (bookingId, token) => {
  try {
    const url = `${baseUrl}bookings/${bookingId}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to delete booking: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting booking:", error);
    throw error;
  }
};

export const approveBooking = async (id, token) => {
  const status = "approved";
  const url = `${baseUrl}users/bookings/all/${id}`;

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id, status }),
    });

    if (!response.ok) {
      const errorMessage = await handleApiError(response, "Failed to approve booking");
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error approving booking:", error);
    throw error;
  }
};

export const cancelBooking = async (id, token) => {
  const status = "rejected";
  const url = `${baseUrl}users/bookings/all/${id}`;

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id, status }),
    });

    if (!response.ok) {
      const errorMessage = await handleApiError(response, "Failed to cancel booking");
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error canceling booking:", error);
    throw error;
  }
};

export const updateBookingStatus = async (bookingId, status, token) => {
  try {
    const response = await fetch(`${baseUrl}users/bookings/all/${bookingId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw new Error("Failed to update booking status");
    }

    const data = await response.json();
    console.log("Status updated successfully:", data);
  } catch (error) {
    console.error("Error updating status:", error);
  }
};

// services/booking.services.js - Add this function


export const fetchPlaceDates = async (spaceId, startDate, endDate, token = null) => {
  try {
    const url = `${baseUrl}bookings/places/${spaceId}/dates`;
    
    const headers = {
      "Content-Type": "application/json",
    };
    
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        spaceID: spaceId,
        start_date: startDate,
        end_date: endDate,
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      const error = new Error("Failed to fetch place dates");
      error.data = data.errors || [];
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error("Error fetching place dates:", error);
    throw error;
  }
};